import { useCallback, useRef, useState, type ReactNode } from "react";
import {
  search_modifier_categories,
  sort_modifier_categories,
} from "src/store/features/modifier-categories-slice";
import { useDispatch } from "react-redux";
import SearchBar from "src/components/common/SearchBar";
import SortbyDropdown from "src/components/common/SortbyDropdown";
import SortIcon from "src/assests/images/dashboard/swap.png";
import useOutsideClick from "src/hooks/useOutsideClick";

export interface ModifierCategoriesHeaderProps {
  /** Rendered beside “Sort by” (e.g. Add category). On small screens they share one row. */
  trailingSlot?: ReactNode;
  /** Active / Inactive / All tabs */
  statusFilterSlot?: ReactNode;
  /** When set, used instead of dispatching search_modifier_categories (parent can track term for refetch). */
  onSearchChange?: (value: string) => void;
}

const ModifierCategoriesHeader = ({
  trailingSlot,
  statusFilterSlot,
  onSearchChange,
}: ModifierCategoriesHeaderProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useOutsideClick({
    ref: dropdownRef,
    handler: () => setIsDropdownOpen(false),
  });

  const handleModifierCategoriesSearch = useCallback(
    (searchModifierCategoryValue: string) => {
      if (onSearchChange) {
        onSearchChange(searchModifierCategoryValue);
      } else {
        dispatch(search_modifier_categories(searchModifierCategoryValue));
      }
    },
    [dispatch, onSearchChange]
  );

  const handleToggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="min-w-0 w-full sm:w-[280px] sm:max-w-[280px] sm:flex-none">
        <SearchBar
          placeholder="Search category by name..."
          onChange={handleModifierCategoriesSearch}
        />
      </div>
      {statusFilterSlot != null ? (
        <div className="w-full min-w-0 sm:w-auto sm:shrink-0">{statusFilterSlot}</div>
      ) : null}
      <div className="flex w-full min-w-0 flex-row items-stretch gap-2 sm:w-auto sm:shrink-0 sm:items-center sm:gap-3">
        <div className="relative min-w-0 flex-1 sm:flex-none" ref={dropdownRef}>
          <button
            type="button"
            className={`flex h-9 w-full min-w-0 items-center justify-center gap-2 rounded-xl border border-[#FD7657] px-2 text-sm duration-500 sm:w-[118px] sm:px-0 ${
              isDropdownOpen
                ? "bg-[#FD76571A] text-[#FD7657]"
                : "bg-transparent text-[#FD7657] hover:bg-white hover:text-orange"
            }`}
            onClick={handleToggleDropdown}
          >
            <img className="h-6 w-6 shrink-0" src={SortIcon} alt="" />
            <span className="truncate">Sort by</span>
          </button>
          {isDropdownOpen && <SortbyDropdown sortBy={sort_modifier_categories} />}
        </div>
        {trailingSlot != null && (
          <div className="min-w-0 flex-1 sm:flex-none sm:min-w-0">{trailingSlot}</div>
        )}
      </div>
    </div>
  );
};

export default ModifierCategoriesHeader;
